import { useReactFlow, Position, NodeToolbar } from "@xyflow/react"
import { NodeResizeControl } from "@xyflow/react"
import {
  TriangleRight,
  Trash,
  Copy,
  Lock,
  Unlock,
  Minimize,
  Maximize,
  Rocket,
  UserPlus,
  Settings,
  Filter,
  Code,
  LucideScroll,
  LucideScrollText,
  Thermometer,
  History,
  BrainCircuit,
  FileText,
  RefreshCw,
  File,
  Network,
  ArrowRight,
  ArrowLeft,
  Plus,
  Eye
} from "lucide-react"
import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
  ReactNode,
  useMemo,
  memo
} from "react"
import { useParams } from "react-router"
import { BaseHandle } from "@/components/handles/base-handle"
import AIUsageIndicator from "./AIUsageIndicator"

import {
  NodeHeader,
  NodeHeaderTitle,
  NodeHeaderActions,
  NodeHeaderMenuAction
} from "@/components/nodes/base/base-container/node-header"

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from "@/components/ui/alert-dialog"
import {
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator
} from "@/components/ui/dropdown-menu"

import { nodeColor } from "@/constants/nodeTypes"
import { useUser } from "@/context/UserContext"
import { useNodeCreation } from "@/hooks/useNodeCreation"
import { useNodeMinimize } from "@/hooks/useNodeMinimize"
import {
  AICommandType,
  AICommandStatus,
  createAICommand
} from "@/utils/aiCommandUtils"
import { NodeAnalysisAndRecommendationsDialog } from "../../../dialogs/analysis-and-recommendations-dialog/NodeAnalysisAndRecommendationsDialog"
import styles from "@/styles/NodeBaseContainer.module.css"
import { NodeHistoryDialog } from "../../history-dialog/NodeHistoryDialog"
import { DIKWDialog } from "../../../dialogs/dikw-dialog"
import { BaseNode } from "../base-node/base-node"
import PresenceIndicator from "../../presence/PresenceIndicator"
import { NodeConnectionMenu } from './NodeConnectionMenu'
import { appendOffsetOfLegend } from "recharts/types/util/ChartUtils"
import { useInitializedSyncedProjectStore } from "../../../../stores/syncedProjectStore"

interface CustomButton {
  id: string
  onClick: () => void
  icon: ReactNode
  tooltip: string
  disabled?: boolean
}

export interface NodeBaseContainerProps {
  id: string
  children: React.ReactNode
  resizable?: boolean
  minWidth?: number
  minHeight?: number
  customStyle?: React.CSSProperties
  status?: "success" | "loading" | "error" | "initial"
  isProcessing?: boolean
  onSizeChange?: (size: { width: number; height: number }) => void
  className?: string
  selected?: boolean
  nodeTitle?: string
  statusMessage?: string
  showDebugInfo?: boolean
  assignedEmails?: string[]
  isLocked?: boolean
  isMinimized?: boolean
  minimizeLevel?: "normal" | "medium" | "minimum"
  onLockChange?: (locked: boolean) => void
  onMinimizeChange?: (minimized: boolean) => void
  onMinimizeLevelChange?: (level: "normal" | "medium" | "minimum") => void
  onTitleChange?: (title: string) => void
  customButtons?: {
    id: string
    onClick: () => void
    icon: React.ReactNode
    tooltip: string
    disabled?: boolean
  }[]
  onAssignUsersClick?: (emails: string[]) => void
  onDataSettingsClick?: () => void
  onConfigClick?: () => void
  onToggleDebugInfo?: () => void
  onHistoryRestore?: (historyData: any) => void
  enableHistory?: boolean
  data?: any
  levelOfUsage?: "Guided" | "Open" | "TotalFreedom"
  onLevelOfUsageChange?: (mode: "Guided" | "Open" | "TotalFreedom") => void
  analysisAndRecommendations?: boolean
  analysisResults?: any
  lastAnalyzedAt?: string
  onAnalysisToggle?: (enabled: boolean) => void
  onAnalysisRun?: () => void
  dataTour?: any
}

const controlStyle = {
  background: "transparent",
  border: "none",
  zIndex: 1
}

const getDefaultInformationClassification = (nodeType: string): string => {
  const nodeTypeToClassificationMap: Record<string, string> = {
    // Data (Raw Resources)
    nodecustom: "Data",
    noderesource: "Data",
    nodewebextract: "Data",

    // Information (Structured Data)
    nodequickstart: "Information",
    nodequickstartdynamic: "Information",
    nodechat: "Information",
    nodediagrams: "Information",
    nodeformwizard: "Information",
    nodepresence: "Information",

    // Knowledge (Processed Information)
    noderesearcher: "Knowledge",
    nodesmartnotes: "Knowledge",
    nodefileminer: "Knowledge",
    nodecomparefiles: "Knowledge",
    commandnodechat: "Knowledge",
    nodeknowledgeset: "Knowledge",

    // Synthesis (Combined Knowledge)
    nodedocument: "Synthesis",
    nodedocumentdesigner: "Synthesis",
    nodestrategy: "Synthesis", // Fixed: was 'Synhesis' (typo)

    // Wisdom (Strategic Intelligence)
    nodeintellica: "Wisdom",
    noderecommendator: "Wisdom",
    nodeagency: "Wisdom",
    nodeagent: "Wisdom",
    nodeglobalconstants: "Wisdom",

    // Deliverable (Final Outputs) - note: singular form
    nodecomposite: "Deliverable",
    nodejavascript: "Deliverable", // Fixed: was 'Deliverables' (plural)
    nodeimagegenerator: "Deliverable" // Fixed: was 'Deliverables' (plural)
  }

  return nodeTypeToClassificationMap[nodeType?.toLowerCase()] || "Data"
}

const NodeBaseContainer = React.forwardRef<
  HTMLDivElement,
  NodeBaseContainerProps
>(
  (
    {
      children,
      resizable = false,
      minWidth = 900,
      minHeight = 630,
      customStyle = {},
      id,
      status = "initial",
      onSizeChange,
      className,
      selected,
      isProcessing,
      nodeTitle = "Untitled Node",
      isLocked = false,
      isMinimized = false,
      minimizeLevel = "normal",
      onLockChange,
      onMinimizeChange,
      onMinimizeLevelChange,
      onTitleChange,
      customButtons = [],
      assignedEmails = [],
      onAssignUsersClick,
      onDataSettingsClick,
      onConfigClick,
      onToggleDebugInfo,
      onHistoryRestore,
      enableHistory = true,
      showDebugInfo = false,
      data = {},
      levelOfUsage,
      onLevelOfUsageChange,
      analysisAndRecommendations = false,
      analysisResults,
      lastAnalyzedAt,
      onAnalysisToggle,
      onAnalysisRun,
      dataTour
    },
    ref
  ) => {
    const internalRef = useRef<HTMLDivElement>(null)
    const titleInputRef = useRef<HTMLInputElement>(null)

    const setRefs = (node: HTMLDivElement) => {
      internalRef.current = node
      if (typeof ref === "function") ref(node)
      else if (ref)
        (ref).current = node
    }

    const { setNodes, getNode, setCenter, setEdges, getEdges } = useReactFlow()
    const user = useUser()
    const { projectId } = useParams<{ projectId: string }>()
    const [nodeType, setNodeType] = useState("")
    const [nodeHeight, setNodeHeight] = useState(minHeight)
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
    const [isHistoryDialogOpen, setIsHistoryDialogOpen] = useState(false)
    const [isAnalysisDialogOpen, setIsAnalysisDialogOpen] = useState(false)
    const [isDIKWDialogOpen, setIsDIKWDialogOpen] = useState(false)
    const [localIsLocked, setLocalIsLocked] = useState(isLocked)
    const [localIsMinimized, setLocalIsMinimized] = useState(isMinimized)
    const [localMinimizeLevel, setLocalMinimizeLevel] = useState<
      "normal" | "medium" | "minimum"
    >(minimizeLevel)
    const [isEditingTitle, setIsEditingTitle] = useState(false)
    const [editingTitleValue, setEditingTitleValue] = useState(nodeTitle || "")
    const [labelLocked, setLabelLocked] = useState(
      typeof data.labellocked === "boolean" ? data.labellocked : false
    )

    const activeUsers = useMemo(
      () => data?.activeUsers || {},
      [data?.activeUsers]
    )

    const classification =
      data?.informationClassification ||
      getDefaultInformationClassification(nodeType) ||
      "Data"
    const classificationKey = (classification || "data").toLowerCase()

    const classificationShadow = useMemo(() => {
      switch (classification) {
        case "Deliverable":
          return "0 20px 32px rgba(0,0,0,0.6)"
        case "Synthesis":
          return "0 16px 28px rgba(0,0,0,0.5)"
        case "Wisdom":
          return "0 12px 24px rgba(0,0,0,0.4)"
        case "Knowledge":
          return "0 8px 16px rgba(0,0,0,0.3)"
        case "Information":
          return "0 4px 8px rgba(0,0,0,0.2)"
        case "Data":
        default:
          return "0 1px 3px rgba(0,0,0,0.1)"
      }
    }, [classification])

    //const isProcessing = data?.documentCommand?.processing === true;
    const processingStatus =
      data?.documentCommand?.statusMessage || "Processing..."

    const [animateClass, setAnimateClass] = useState("")
    const [hasMounted, setHasMounted] = useState(false)

    useEffect(() => {
      if (!hasMounted) {
        const delay = Math.random() * 300 // random delay up to 300ms for cascade
        const timeout = setTimeout(() => {
          setAnimateClass("node-init-animation")
          setHasMounted(true)
          const clearAnim = setTimeout(() => setAnimateClass(""), 500)
          return () => clearTimeout(clearAnim)
        }, delay)
        return () => clearTimeout(timeout)
      }
    }, [hasMounted])

    useEffect(() => {
      if (data.__justCreated) {
        setAnimateClass("node-create-animation")
        const timeout = setTimeout(() => setAnimateClass(""), 500)
        return () => clearTimeout(timeout)
      }
    }, [data.__justCreated])

    useEffect(() => {
      if (data.__justDeleted) {
        setAnimateClass("node-delete-animation")
        const timeout = setTimeout(() => setAnimateClass(""), 500)
        return () => clearTimeout(timeout)
      }
    }, [data.__justDeleted])

    useEffect(() => {
      if (data.__justInitialized) {
        setAnimateClass("node-init-animation")
        const timeout = setTimeout(() => setAnimateClass(""), 500)
        return () => clearTimeout(timeout)
      }
    }, [data.__justInitialized])

    useEffect(() => {
      setLocalIsLocked(isLocked)
    }, [isLocked])

    useEffect(() => {
      setLocalIsMinimized(isMinimized)
    }, [isMinimized])

    useEffect(() => {
      setLocalMinimizeLevel(minimizeLevel)
    }, [minimizeLevel])

    useEffect(() => {
      setEditingTitleValue(nodeTitle || "")
    }, [nodeTitle])

    useEffect(() => {
      setLabelLocked(
        typeof data.labellocked === "boolean" ? data.labellocked : false
      )
    }, [data.labellocked])

    useEffect(() => {
      const node = getNode(id)
      setNodeType(node.type)
      if (node && node.height) {
        setNodeHeight(node.height)
      }
    }, [getNode, id])

    useEffect(() => {
      if (internalRef.current && onSizeChange) {
        const { clientWidth, clientHeight } = internalRef.current
        onSizeChange({ width: clientWidth, height: clientHeight })
      }
    }, [onSizeChange])

    const zoomNear = useCallback(() => {
      const node = getNode(id)
      if (node && node.position && node.measured && node.measured.width !== undefined) {
        const nodePosition = node.position
        const nodeWidth = node.measured.width
        setCenter(nodePosition.x + nodeWidth / 2, nodePosition.y + 400, {
          zoom: 1,
          duration: 430
        })
      }
    }, [getNode, id, setCenter])

    const handleDelete = () => {
      setIsDeleteDialogOpen(true)
    }

    const confirmDelete = () => {
      if ((window as any).__flowDeleteNode) {
        ; (window as any).__flowDeleteNode(id)
      }
      setIsDeleteDialogOpen(false)
    }

    const handleDuplicate = () => {
      if ((window as any).__flowDuplicateNode) {
        ; (window as any).__flowDuplicateNode(id)
      }
    }

    const handleLock = () => {
      const newState = !localIsLocked
      setLocalIsLocked(newState)
      updateNodeData({ isLocked: newState })
      if (onLockChange) {
        onLockChange(newState)
      }
    }

    const { toggleMinimizeNode } = useNodeMinimize()

    // Helper function to add nodes to the flow
    const addNode = useCallback(
      (node: any) => {
        setNodes((nodes) => [...nodes, node])
      },
      [setNodes]
    )

    const handleLinkNodes = useCallback(
      (nodeIds: string[]) => {
        if (nodeIds.length > 0 && id) {
          setNodes((nodes) => {
            const updated = nodes.map((node) => {
              if (node.id === id) {
                // --- linkedKnowledgeNodes (old format) ---
                const linkedKnowledgeNodes = [
                  ...(Array.isArray(node.data?.linkedKnowledgeNodes)
                    ? node.data.linkedKnowledgeNodes
                    : [])
                ]
                // --- inputLinks (old format) ---
                let inputLinks: any[] = [];
                if (
                  node.data &&
                  typeof node.data === 'object' &&
                  node.data.dataSettings &&
                  typeof node.data.dataSettings === 'object' &&
                  Array.isArray(node.data.dataSettings.inputLinks)
                ) {
                  inputLinks = [...node.data.dataSettings.inputLinks];
                }
                nodeIds.forEach((linkedId) => {
                  // Avoid duplicates by nodeId
                  if (
                    !linkedKnowledgeNodes.some(
                      (ln: any) =>
                        (typeof ln === "object" && ln.nodeId === linkedId) ||
                        ln === linkedId
                    )
                  ) {
                    const linkedNode = getNode(linkedId)
                    if (linkedNode) {
                      // --- Ensure all fields in data are enabled ---
                      const linkedData = { ...(linkedNode.data || {}) }
                      if (linkedData.dataSettings?.documentSections?.sections) {
                        Object.entries(
                          (linkedData.dataSettings && linkedData.dataSettings.documentSections && linkedData.dataSettings.documentSections.sections)
                        ).forEach(([title, section]: [string, any]) => {
                          if (section) section.enabled = true
                        })
                      }
                      if (
                        linkedData.dataSettings &&
                        typeof linkedData.dataSettings === 'object' &&
                        linkedData.dataSettings.documentSections &&
                        typeof linkedData.dataSettings.documentSections === 'object' &&
                        linkedData.dataSettings.documentSections.fullDocument &&
                        typeof linkedData.dataSettings.documentSections.fullDocument === 'object'
                      ) {
                        linkedData.dataSettings.documentSections.fullDocument.enabled = true
                      }
                      if (linkedData.dataSettings?.exposedData) {
                        Object.values(
                          linkedData.dataSettings.exposedData
                        ).forEach((field: any) => {
                          if (field) field.enabled = true
                        })
                      }
                      // --- End ensure enabled ---

                      // Add to linkedKnowledgeNodes
                      linkedKnowledgeNodes.push({
                        data: linkedData,
                        nodeId: linkedNode.id,
                        nodeType: linkedNode.type,
                        enabled: true,
                        nodeName:
                          linkedNode.data?.label ||
                          linkedNode.data?.nodename ||
                          linkedNode.id
                      })
                      // Add to inputLinks (avoid duplicates)
                      if (
                        !inputLinks.some(
                          (il: any) =>
                            il.nodeId === linkedNode.id ||
                            (il.nodeId && il.nodeId.value === linkedNode.id)
                        )
                      ) {
                        // Build a flat data object with all fields enabled
                        const inputLinkData: Record<string, any> = {}
                        // Full Document
                        if (linkedData.contentMarkdown) {
                          inputLinkData.fullDocument = {
                            value: linkedData.contentMarkdown,
                            enabled: true,
                            label: "Full Document Content",
                            description: "The complete document content"
                          }
                        }
                        // Sections
                        if (
                          linkedData.dataSettings &&
                          typeof linkedData.dataSettings === 'object' &&
                          linkedData.dataSettings.documentSections &&
                          typeof linkedData.dataSettings.documentSections === 'object' &&
                          linkedData.dataSettings.documentSections.sections &&
                          typeof linkedData.dataSettings.documentSections.sections === 'object'
                        ) {
                          Object.entries(
                            linkedData.dataSettings.documentSections.sections
                          ).forEach(([title, section]: [string, any]) => {
                            inputLinkData[`section:${title}`] = {
                              value: section.value || "",
                              enabled: true,
                              label: `Section: ${title}`,
                              description: `Document section: ${title}`
                            }
                          })
                        }
                        // Exposed Data
                        if (linkedData.dataSettings?.exposedData) {
                          Object.entries(
                            linkedData.dataSettings.exposedData
                          ).forEach(([key, field]: [string, any]) => {
                            inputLinkData[`exposed:${key}`] = {
                              value: field.value || "",
                              enabled: true,
                              label: `${field.label || key} (Exposed)`,
                              description:
                                field.description || `Exposed field: ${key}`,
                              permissions: field.permissions || {
                                readable: true,
                                writable: false
                              }
                            }
                          })
                        }
                        // NodeId/NodeType
                        inputLinkData.nodeId = {
                          value: linkedNode.id,
                          enabled: false,
                          label: "Node ID"
                        }
                        inputLinkData.nodeType = {
                          value: linkedNode.type || "unknown",
                          enabled: false,
                          label: "Node Type"
                        }

                        // --- Enable special fields for specific nodetypes ---
                        if (linkedNode.type === "nodediagrams") {
                          if (linkedData.diagramContent) {
                            inputLinkData.diagramContent = {
                              value: linkedData.diagramContent,
                              enabled: true,
                              label: "Diagram Content",
                              description: "PlantUML or diagram source"
                            }
                          }
                          if (linkedData.designPrompt) {
                            inputLinkData.designPrompt = {
                              value: linkedData.designPrompt,
                              enabled: true,
                              label: "Design Prompt",
                              description: "Prompt used for diagram generation"
                            }
                          }
                          if (linkedData.diagramType) {
                            inputLinkData.diagramType = {
                              value: linkedData.diagramType,
                              enabled: true,
                              label: "Diagram Type",
                              description: "Type of diagram"
                            }
                          }
                        }
                        if (linkedNode.type === "nodewebextract") {
                          if (linkedData.url) {
                            inputLinkData.url = {
                              value: linkedData.url,
                              enabled: true,
                              label: "URL",
                              description: "Source URL"
                            }
                          }
                          if (linkedData.extractedContent) {
                            inputLinkData.extractedContent = {
                              value: linkedData.extractedContent,
                              enabled: true,
                              label: "Extracted Content",
                              description: "Extracted web content"
                            }
                          }
                        }
                        if (linkedNode.type === "nodedocument") {
                          if (linkedData.contentMarkdown) {
                            inputLinkData.contentMarkdown = {
                              value: linkedData.contentMarkdown,
                              enabled: true,
                              label: "Document Markdown",
                              description: "Document content in Markdown"
                            }
                          }
                        }
                        // --- End special fields ---

                        inputLinks.push({
                          data: inputLinkData,
                          nodeId: linkedNode.id,
                          nodeType: linkedNode.type,
                          enabled: true,
                          nodeName:
                            linkedNode.data?.label ||
                            linkedNode.data?.nodename ||
                            linkedNode.id
                        })
                      }
                    }
                  }
                })
                const updatedNode = {
                  ...node,
                  data: {
                    ...node.data,
                    linkedKnowledgeNodes,
                    dataSettings: {
                      ...(node.data?.dataSettings || {}),
                      inputLinks
                    }
                  }
                }
                return updatedNode
              }
              return node
            })
            return updated
          })
        }
      },
      [id, setNodes, getNode]
    )

    // Helper function to create edges between nodes
    const createEdgesForNodes = (
      sourceNodeId: string,
      targetNodeIds: string[]
    ) => {
      const newEdges = targetNodeIds.map((targetId) => ({
        id: `edge-${sourceNodeId}-${targetId}`,
        source: sourceNodeId,
        target: targetId,
        type: "default",
        animated: false
      }))
      // Add edges to the flow
      setTimeout(() => {
        setEdges((currentEdges: any[]) => [...currentEdges, ...newEdges])
      }, 100)
    }

    const handleCreateRecommendedNodes = useCallback(
      async (dataPoints: any[], selectedIndices: number[]) => {
        try {
          const selectedDataPoints = selectedIndices.map(
            (index) => dataPoints[index]
          )
          const createdNodeIds: string[] = []
          let induxliini = -1
          for (const dataPoint of selectedDataPoints) {
            induxliini++
            const nodeData = dataPoint.data || {}

            let newNodeId = ""
            const node = getNode(id)
            // Create position offset for better visual organization
            const basePosition = {
              x: node.position.x + node.width + 50 + induxliini * 900,
              y: node.position.y
            }

            switch (dataPoint.nodetype) {
              case "nodedocument": {
                // Inline Document Node creation
                const position = basePosition
                const newNode: any = {
                  id: `document-node-${new Date().toISOString().replace(/[-:.TZ]/g, "")}`,
                  type: "nodedocument",
                  position,
                  width: 1200,
                  height: 1600,
                  data: {
                    label: dataPoint.nodename || "Document Node",
                    informationClassification: "Synthesis",
                    contentMarkdown: nodeData.contentMarkdown || "",
                    createdBy: user.email,
                    createdAt: new Date().toISOString(),
                    analysisAndRecommendations: true
                  },
                  draggable: true,
                  selectable: true,
                  connectable: true
                }
                addNode(newNode)
                newNodeId = newNode.id
                break
              }
              case "nodediagrams": {
                // Inline Diagram Node creation
                const diagramNode: any = {
                  id: `diagram-node-${new Date().toISOString().replace(/[-:.TZ]/g, "")}`,
                  type: "nodediagrams",
                  position: basePosition,
                  width: 900,
                  height: 630,
                  data: {
                    label: dataPoint.nodename || "PlantUML Diagram",
                    informationClassification: "Information",
                    createdBy: user.email,
                    createdAt: new Date().toISOString(),
                    diagramContent: nodeData.diagramContent || "",
                    designPrompt: nodeData.designPrompt || "",
                    diagramType: nodeData.diagramType || ""
                  }
                }
                addNode(diagramNode)
                newNodeId = diagramNode.id
                break
              }
              case "nodewebextract": {
                // Inline Web Extract Node creation
                const position = basePosition
                const newNode: any = {
                  id: `web-extract-node-${new Date().toISOString().replace(/[-:.TZ]/g, "")}`,
                  type: "nodewebextract",
                  position,
                  width: 900,
                  height: 630,
                  data: {
                    label: dataPoint.nodename || "Web Extract",
                    informationClassification: "Data",
                    createdBy: user.email,
                    createdAt: new Date().toISOString(),
                    url: nodeData.url || "",
                    mode: "normal",
                    includeImages: false,
                    extractedContent: nodeData.extractContent || "",
                    images: [],
                    lastRefreshed: "",
                    automationEnabled: false,
                    schedule: null,
                    statusMessage: ""
                  },
                  draggable: true,
                  selectable: true,
                  connectable: true
                }
                addNode(newNode)
                newNodeId = newNode.id
                break
              }
              default: {
                // Only fallback for truly unknown types
                break
              }
            }
            if (newNodeId) {
              createdNodeIds.push(newNodeId)
            }
          }

          // Defer linking until after nodes are added to state
          if (createdNodeIds.length > 0 && id) {
            setTimeout(() => {
              handleLinkNodes(createdNodeIds)
              createEdgesForNodes(id, createdNodeIds)
            }, 0)
          }
        } catch (error) {
          console.error("Error creating recommended nodes:", error)
        }
      },
      [getNode, addNode, user.email, id, handleLinkNodes, createEdgesForNodes]
    )

    const handleCopyToContext = useCallback(
      (selectedRecommendations: string[]) => {
        if (selectedRecommendations.length === 0) return

        const recommendationsText = selectedRecommendations
          .map((rec, index) => `${index + 1}. ${rec}`)
          .join("\n")

        const contextText = `Analysis Recommendations:\n${recommendationsText}\n\nPlease consider these recommendations when updating the content.`

        setNodes((nodes) =>
          nodes.map((node) => {
            if (node.id === id) {
              const currentContext = node.data?.context || ""
              const separator = currentContext ? "\n\n---\n\n" : ""
              return {
                ...node,
                data: {
                  ...node.data,
                  context: currentContext + separator + contextText
                }
              }
            }
            return node
          })
        )

        navigator.clipboard.writeText(contextText).catch(() => { })
      },
      [id, setNodes]
    )

    const handleMinimizeToggle = () => {
      // Get current node state
      const node = getNode(id)
      if (!node) return

      // Cycle through minimize levels: normal -> medium -> minimum -> normal
      const currentLevel = node.data?.minimizeLevel || "normal"
      let nextLevel: "normal" | "medium" | "minimum"

      switch (currentLevel) {
        case "normal":
          nextLevel = "medium"
          break
        case "medium":
          nextLevel = "minimum"
          break
        case "minimum":
          nextLevel = "normal"
          break
        default:
          nextLevel = "medium"
      }

      // Set local state for UI updates
      setLocalMinimizeLevel(nextLevel)
      setLocalIsMinimized(nextLevel !== "normal")

      // Calculate new dimensions based on minimize level
      let newWidth: number
      let newHeight: number

      switch (nextLevel) {
        case "minimum":
          newWidth = 300
          newHeight = 50
          break
        case "medium":
          newWidth = 350
          // Calculate dynamic height based on minified info content
          const minifiedEntries = data.minifiedInfo
            ? Object.entries(data.minifiedInfo).filter(
              ([key, value]) =>
                value && typeof value === "string" && value.trim().length > 0
            )
            : []
          // Base height: header (40px) + padding (16px) + each entry (~40px) + container padding (24px)
          const calculatedHeight = Math.max(
            100,
            80 + minifiedEntries.length * 80
          )
          newHeight = Math.min(calculatedHeight, 300) // Cap at 300px max
          break
        case "normal":
          newWidth = node.data?.originalWidth || minWidth
          newHeight = node.data?.originalHeight || minHeight
          break
      }

      // Update React Flow node dimensions and data
      setNodes((nodes) =>
        nodes.map((n) => {
          if (n.id === id) {
            return {
              ...n,
              width: newWidth,
              height: newHeight,
              data: {
                ...n.data,
                minimizeLevel: nextLevel,
                isMinimized: nextLevel !== "normal",
                updatedBy: user.email
              }
            }
          }
          return n
        })
      )

      // Call the callbacks if provided
      if (onMinimizeChange) {
        onMinimizeChange(nextLevel !== "normal")
      }
      if (onMinimizeLevelChange) {
        onMinimizeLevelChange(nextLevel)
      }
    }

    const handleAssignUsers = () => {
      if (onAssignUsersClick) {
        onAssignUsersClick(assignedEmails)
      } else {
        const emails = window.prompt(
          "Enter emails separated by space",
          assignedEmails.join(" ")
        )
        if (emails !== null) {
          updateNodeData({
            assignedEmails: emails.split(" ").filter((e) => e.trim())
          })
        }
      }
    }

    const updateNodeData = (newData: any) => {
      if (
        typeof newData !== "object" ||
        newData === null ||
        Array.isArray(newData)
      ) {
        console.error("updateNodeData called with non-object:", newData)
        return
      }
      setNodes((nodes) =>
        nodes.map((node) => {
          if (node.id === id) {
            return {
              ...node,
              data: {
                ...node.data,
                ...newData,
                updatedBy: user.email // <-- Add this line
              }
            }
          }
          return node
        })
      )
    }

    const handleTitleClick = () => {
      if (!isProcessing && !localIsLocked) {
        setIsEditingTitle(true)
      }
    }

    const [showLockIcon, setShowLockIcon] = useState(false)
    const lockIconTimeout = useRef<NodeJS.Timeout | null>(null)

    useEffect(() => {
      // Show lock icon if locked, or while editing
      if (isEditingTitle || labelLocked) {
        setShowLockIcon(true)
        if (lockIconTimeout.current) {
          clearTimeout(lockIconTimeout.current)
          lockIconTimeout.current = null
        }
      }
    }, [isEditingTitle, labelLocked])

    const handleTitleSave = () => {
      setIsEditingTitle(false)
      // Show lock icon for 0.5s after editing if not locked
      if (!labelLocked) {
        setShowLockIcon(true)
        if (lockIconTimeout.current) clearTimeout(lockIconTimeout.current)
        lockIconTimeout.current = setTimeout(() => setShowLockIcon(false), 500)
      }
      // Save label and lock state if changed
      const labelToSave =
        editingTitleValue.trim() === "" ? "Not defined" : editingTitleValue
      if (labelToSave !== nodeTitle || labelLocked !== !!data.labellocked) {
        updateNodeData({ label: labelToSave, labellocked: labelLocked })
        if (onTitleChange) {
          onTitleChange(labelToSave)
        }
      }
    }

    const handleTitleKeyPress = (e: React.KeyboardEvent) => {
      if (e.key === "Enter") {
        handleTitleSave()
      } else if (e.key === "Escape") {
        setIsEditingTitle(false)
        setEditingTitleValue(nodeTitle || "")
        setLabelLocked(
          typeof data.labellocked === "boolean" ? data.labellocked : false
        )
        if (!labelLocked) {
          setShowLockIcon(true)
          if (lockIconTimeout.current) clearTimeout(lockIconTimeout.current)
          lockIconTimeout.current = setTimeout(
            () => setShowLockIcon(false),
            500
          )
        }
      }
    }

    // Restore the previous working lock toggle logic (double click required, but works)
    const handleLabelLockToggle = () => {
      setLabelLocked((prev) => !prev)
    }

    useEffect(() => {
      if (isEditingTitle && titleInputRef.current) {
        titleInputRef.current.focus()
        titleInputRef.current.select()
      }
    }, [isEditingTitle])

    const validateEmails = (emails: string | string[]): boolean => {
      if (typeof emails === "string") {
        if (!emails.trim()) return true
        const emailArray = emails.split(" ")
        const emailRegex = /^\S+@\S+\.\w+$/i
        return emailArray.every((email) => emailRegex.test(email))
      } else {
        if (emails.length === 0) return true
        const emailRegex = /^\S+@\S+\.\w+$/i
        return emails.every((email) => emailRegex.test(email))
      }
    }

    const hasValidAssignedEmails =
      assignedEmails &&
      assignedEmails.length > 0 &&
      validateEmails(assignedEmails)

    const currentStatus = isProcessing ? "loading" : status || "initial"

    const normalizedAssignedEmails = useMemo(() => {
      if (!assignedEmails) return []
      if (Array.isArray(assignedEmails)) return assignedEmails
      if (typeof assignedEmails === "string") {
        return assignedEmails.trim().split(/\s+/).filter(Boolean)
      }
      return []
    }, [assignedEmails])

    const {
      useNodesStateSynced
    } = useInitializedSyncedProjectStore();

    const [
      nodes,
      setNodesSynced,
      setNodeSynced,
      onNodesChange,
      undoManager,
      startDrag,
      stopDrag,
      forceSyncNode,
      startResize,
      stopResize
    ] = useNodesStateSynced(user?.email || null);

    const onResizeEnd = useCallback(
      (evt, { width, height }) => {
        stopResize(id);
        if (onSizeChange) onSizeChange({ width, height });
      },
      [stopResize, id, onSizeChange]
    );

    const handleNextMode = () => {
      if (!levelOfUsage || !onLevelOfUsageChange) return
      const modes = ["Guided", "Open", "TotalFreedom"]
      const currentIndex = modes.indexOf(levelOfUsage)
      const nextIndex = (currentIndex + 1) % modes.length
      onLevelOfUsageChange(modes[nextIndex] as any)
    }

    // Custom User Instructions logic
    const [showInstructionsDialog, setShowInstructionsDialog] = useState(false)
    const [customInstructions, setCustomInstructions] = useState(
      data?.customuserinstructions || ""
    )

    useEffect(() => {
      setCustomInstructions(data?.customuserinstructions || "")
    }, [data?.customuserinstructions])

    const handleInstructionsClick = () => {
      setShowInstructionsDialog(true)
    }

    const handleInstructionsSave = (value: string) => {
      setShowInstructionsDialog(false)
      setCustomInstructions(value)
      updateNodeData({ customuserinstructions: value })
    }

    const handleInstructionsCancel = () => {
      setShowInstructionsDialog(false)
    }

    // --- Node-specific AI temperature logic ---
    const [showTemperatureDialog, setShowTemperatureDialog] = useState(false)
    const [bypassDefaultTemperature, setBypassDefaultTemperature] = useState(
      typeof data?.customcreativeness === "number"
    )
    const [localTemperature, setLocalTemperature] = useState(
      typeof data?.customcreativeness === "number"
        ? Math.round((data.customcreativeness || 0) * 100)
        : 50
    )

    useEffect(() => {
      if (typeof data?.customcreativeness === "number") {
        setBypassDefaultTemperature(true)
        setLocalTemperature(Math.round(data.customcreativeness * 100))
      } else {
        setBypassDefaultTemperature(false)
        setLocalTemperature(50)
      }
    }, [data?.customcreativeness])

    const handleTemperatureDialogOpen = () => {
      setShowTemperatureDialog(true)
    }

    const handleTemperatureDialogClose = () => {
      setShowTemperatureDialog(false)
    }

    const handleTemperatureSave = () => {
      if (bypassDefaultTemperature) {
        updateNodeData({ customcreativeness: localTemperature / 100 })
      } else {
        updateNodeData({ customcreativeness: undefined })
      }
      setShowTemperatureDialog(false)
    }

    const handleGenerateTitle = () => {
      // Use the same pattern as QuickStartNode to trigger a title update command
      updateNodeData({
        documentCommand: createAICommand(
          AICommandType.TITLE_UPDATE,
          AICommandStatus.STARTED,
          {
            nodeId: id
          }
        )
      })
    }

    const handleExtractDIKW = () => {
      // Trigger DIKW extraction using documentRequest pattern
      updateNodeData({
        documentRequest: {
          ...(data.documentRequest || {}),
          document: 'dikw-extraction',
          'sysagent-cmd': 'NodeDIKW-Extraction-Started',
          'sysagent-response': '',
          processing: true,
          processedAt: undefined, // Clear any previous processedAt to allow re-processing
          timestamp: Date.now(),
          [id]: { content: data.content || 'No content provided for DIKW extraction' },
        },
        statusMessage: 'Extracting DIKW layers...',
      })
    }

    const handleViewDIKW = () => {
      // Open DIKW dialog to view extracted data
      setIsDIKWDialogOpen(true)
    }

    // Toolbar button handlers
    const handleRefreshAI = () => {
      if (nodeType === 'nodedocument') {
        updateNodeData({
          documentCommand: createAICommand(
            AICommandType.TITLE_UPDATE,
            AICommandStatus.STARTED,
            { nodeId: id, action: 'refresh' }
          )
        })
      }
    }

    const handleLoadTemplate = () => {
      if (nodeType === 'nodedocument') {
        updateNodeData({
          documentRequest: {
            ...(data.documentRequest || {}),
            document: 'load-template',
            'sysagent-cmd': 'NodeDocument-LoadTemplate-Started',
            processing: true,
            timestamp: Date.now()
          },
          statusMessage: 'Loading template...',
        })
      }
    }

    const handleConnections = () => {
      console.log('Show connections for node:', id)
    }

    const handleOutgoingConnections = () => {
      console.log('Show outgoing connections for node:', id)
    }

    const handleExtractSortedKnowledge = () => {
      updateNodeData({
        documentRequest: {
          ...(data.documentRequest || {}),
          document: 'extract-sorted-knowledge',
          'sysagent-cmd': 'NodeKnowledge-Extract-Sort-Started',
          processing: true,
          timestamp: Date.now()
        },
        statusMessage: 'Extracting sorted knowledge...',
      })
    }

    const handleIncomingConnections = () => {
      console.log('Show incoming connections for node:', id)
    }

    const handleCreateSynthesis = () => {
      updateNodeData({
        documentRequest: {
          ...(data.documentRequest || {}),
          document: 'create-synthesis',
          'sysagent-cmd': 'NodeSynthesis-Create-Started',
          processing: true,
          timestamp: Date.now()
        },
        statusMessage: 'Creating synthesis...',
      })
    }

    const handleHistoryDuplicate = useCallback(
      (historyData: any) => {
        // Duplicate the node, then replace the new node's data with historyData.nodeData after a delay
        if ((window as any).__flowDuplicateNode) {
          ; (window as any).__flowDuplicateNode(id, (newNodeId: string) => {
            if (typeof newNodeId === "string") {
              setTimeout(() => {
                setNodes((nodes) =>
                  nodes.map((node) => {
                    if (node.id === newNodeId) {
                      return {
                        ...node,
                        data: {
                          ...node.data,
                          ...historyData.nodeData,
                          updatedBy: user.email
                        }
                      }
                    }
                    return node
                  })
                )
              }, 500)
            }
          })
        }
      },
      [id, setNodes, user.email]
    )

    const handleHistoryRestore = useCallback(
      (historyData: any) => {
        // For restoration, just replace the data in the current node
        updateNodeData(historyData.nodeData)
      },
      [updateNodeData]
    )

    // Add double click handler to zoom in when double clicking anywhere in the node (not just content area)
    const handleNodeDoubleClick = (e: React.MouseEvent<HTMLDivElement>) => {
      // Prevent double click from triggering on resize handle or header buttons
      if (e.target instanceof HTMLElement) {
        // Example: skip if double-clicking on a button or input
        if (
          e.target.closest("button") ||
          e.target.closest("input") ||
          e.target.closest("textarea")
        ) {
          return
        }
      }
      zoomNear()
    }

    // Add mouse enter/leave to set up-down arrow cursor on node
    const [isNodeHovered, setIsNodeHovered] = useState(false)

    const handleNodeMouseEnter = (e: React.MouseEvent<HTMLDivElement>) => {
      // Only set if not over a button/input/textarea
      if (
        e.target instanceof HTMLElement &&
        !e.target.closest("button") &&
        !e.target.closest("input") &&
        !e.target.closest("textarea")
      ) {
        setIsNodeHovered(true)
      }
    }
    const handleNodeMouseLeave = () => setIsNodeHovered(false)

    // Compute node color for border
    const nodeBorderColor = useMemo(
      () => nodeColor({ type: nodeType }),
      [nodeType]
    )

    const divStyle = useMemo(
      () => ({ background: nodeBorderColor }),
      [nodeBorderColor]
    )

    // Render minified info when in medium state
    const renderMinifiedInfo = () => {
      if (localMinimizeLevel !== "medium" || !data.minifiedInfo) return null

      const minifiedEntries = Object.entries(data.minifiedInfo).filter(
        ([key, value]) =>
          value && typeof value === "string" && value.trim().length > 0
      )

      if (minifiedEntries.length === 0) return null

      return (
        <div className={styles.minifiedInfoContainer}>
          {minifiedEntries.map(([key, value]) => (
            <div key={key} className={styles.minifiedInfoItem}>
              <span className={styles.minifiedInfoLabel}>{key}:</span>
              <span className={styles.minifiedInfoValue}>
                {String(value).substring(0, 100)}
                {String(value).length > 100 ? "..." : ""}
              </span>
            </div>
          ))}
        </div>
      )
    }

    // Force layout recalculation when minimize level changes
    useEffect(() => {
      if (internalRef.current) {
        // Force a reflow to ensure proper layout
        const element = internalRef.current
        element.style.display = "none"
        element.offsetHeight // Trigger reflow
        element.style.display = ""
      }
    }, [localMinimizeLevel])

    // Add this method to check and update missing informationClassification
    const ensureInformationClassification = useCallback(() => {
      const node = getNode(id)
      if (!node) return

      // Check if informationClassification is missing or undefined
      if (!data?.informationClassification) {
        const defaultClassification = getDefaultInformationClassification(
          node.type
        )

        // Update the node data with the default classification
        updateNodeData({
          informationClassification: defaultClassification
        })

        console.log(
          `Auto-assigned informationClassification "${defaultClassification}" to node type "${node.type}"`
        )
      }
    }, [id, getNode, data?.informationClassification, updateNodeData])

    // Run the check when component mounts or when node type changes
    useEffect(() => {
      ensureInformationClassification()
    }, [])

    const handleConnect = useCallback((sourceId: string, targetId: string) => {
      const edgeId = `floating-${sourceId}-${targetId}`
      const edgeData = {
        animated: true,
        pattern: 'dashed',
        direction: 'forward',
        color: '#b1b1b7'
      }

      if (sourceId.startsWith('SmartNotes')) {
        edgeData.color = '#ff0072'
      } else if (sourceId.startsWith('Modifier')) {
        edgeData.color = '#00ff72'
      } else if (sourceId.startsWith('Cell')) {
        edgeData.color = '#0072ff'
      } else if (sourceId.startsWith('ChatAI')) {
        edgeData.color = '#7200ff'
      }

      setEdges((eds) => [
        ...eds,
        {
          id: edgeId,
          source: sourceId,
          target: targetId,
          type: 'floating',
          data: edgeData,
          animated: true,
        },
      ])
    }, [setEdges])

    const handleDisconnect = useCallback((edgeId: string) => {
      setEdges((eds) => eds.filter((edge) => edge.id !== edgeId))
    }, [setEdges])

    return (
      <>        {/* Top Toolbar */}
        <NodeToolbar
          isVisible={selected && !localIsMinimized}
          position={Position.Top}
          offset={12}
          className={styles.toolbarTop}
        >          <div className="flex items-center gap-1 p-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-md shadow-lg">            <button
          onClick={handleInstructionsClick}
          className={`hover:bg-gray-100 dark:hover:bg-gray-700 rounded text-xs ${styles.toolbarButton}`}
          title="Custom Instructions"
        >
          <FileText size={14} />
          <span className="text-[10px] mt-0.5">Custom<br />Instructions</span>
        </button>
            <button
              onClick={handleTemperatureDialogOpen}
              className={`hover:bg-gray-100 dark:hover:bg-gray-700 rounded text-xs ${styles.toolbarButton}`}
              title="Creativeness Level"
            >
              <Thermometer size={14} />
              <span className="text-[10px] mt-0.5">Creativeness<br />level</span>
            </button>
            <button
              onClick={handleExtractDIKW}
              className={`hover:bg-gray-100 dark:hover:bg-gray-700 rounded text-xs ${styles.toolbarButton}`}
              title="Extract DIKW"
            >
              <BrainCircuit size={14} />
              <span className="text-[10px] mt-0.5">Extract<br />DIKW</span>
            </button>
            <button
              onClick={handleViewDIKW}
              className={`hover:bg-gray-100 dark:hover:bg-gray-700 rounded text-xs ${styles.toolbarButton}`}
              title="View DIKW"
            >
              <Eye size={14} />
              <span className="text-[10px] mt-0.5">View<br />DIKW</span>
            </button>
            {nodeType === 'nodedocument' && (
              <>
                <button
                  onClick={handleRefreshAI}
                  className={`hover:bg-gray-100 dark:hover:bg-gray-700 rounded text-xs ${styles.toolbarButton}`}
                  title="Refresh (AI)"
                >
                  <RefreshCw size={14} />
                  <span className="text-[10px] mt-0.5">Refresh<br />(AI)</span>
                </button>
                <button
                  onClick={handleLoadTemplate}
                  className={`hover:bg-gray-100 dark:hover:bg-gray-700 rounded text-xs ${styles.toolbarButton}`}
                  title="Load Template"
                >
                  <File size={14} />
                  <span className="text-[10px] mt-0.5">Load<br />template</span>
                </button>
              </>
            )}
            <button
              onClick={handleConnections}
              className={`hover:bg-gray-100 dark:hover:bg-gray-700 rounded text-xs ${styles.toolbarButton}`}
              title="Connections"
            >
              <Network size={14} />
              <span className="text-[10px] mt-0.5">Connections</span>
            </button>
          </div>
        </NodeToolbar>        {/* Right Toolbar */}
        <NodeToolbar
          isVisible={selected && !localIsMinimized}
          position={Position.Right}
          align="start"
          offset={12}
          className={styles.toolbarRight}
        ><div className="flex flex-col gap-1 p-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-md shadow-lg">
            <button
              onClick={handleOutgoingConnections}
              className={`hover:bg-gray-100 dark:hover:bg-gray-700 rounded text-xs ${styles.toolbarButton}`}
              title="Outgoing Connections"
            >
              <ArrowRight size={14} />
              <span className="text-[8px] mt-0.5">Outgoing<br />connections</span>
            </button>
            <button
              onClick={handleExtractSortedKnowledge}
              className={`hover:bg-gray-100 dark:hover:bg-gray-700 rounded text-xs ${styles.toolbarButton}`}
              title="Extract Sorted Knowledge"
            >
              <LucideScroll size={14} />
              <span className="text-[8px] mt-0.5">Extract refined<br />knowledge</span>
            </button>
          </div>
        </NodeToolbar>        {/* Left Toolbar */}
        <NodeToolbar
          isVisible={selected && !localIsMinimized}
          position={Position.Left}
          align="start"
          offset={12}
          className={styles.toolbarLeft}
        ><div className="flex flex-col gap-1 p-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-md shadow-lg">
            <button
              onClick={handleIncomingConnections}
              className={`hover:bg-gray-100 dark:hover:bg-gray-700 rounded text-xs ${styles.toolbarButton}`}
              title="Incoming Connections"
            >
              <ArrowRight size={14} />
              <span className="text-[8px] mt-0.5">Incoming<br />connections</span>
            </button>
            <button
              onClick={handleCreateSynthesis}
              className={`hover:bg-gray-100 dark:hover:bg-gray-700 rounded text-xs ${styles.toolbarButton}`}
              title="Create Synthesis for Missing Data"
            >
              <Plus size={14} />
              <span className="text-[8px] mt-0.5">Recommend input<br />data points</span>
            </button>
          </div>
        </NodeToolbar>
        {isProcessing && (
          <div className={styles.processingOverlay}>
            <div
              className={`processing-spinner ${styles.processingSpinnerMargin}`}
            ></div>
            <div>{processingStatus}</div>
          </div>
        )}
        {hasValidAssignedEmails &&
          !localIsLocked &&
          !isProcessing &&
          (isNodeHovered || selected) && (
            <div className={`${styles.assignedEmails}`}>
              Assigned to:{" "}
              {normalizedAssignedEmails.map((email) => (
                <span key={email} className="mr-1 font-medium">
                  {email}
                </span>
              ))}
            </div>
          )}
        {/* Colored left border */}
        <div className={styles.nodeTypeColorBorder} style={divStyle} />
        <BaseNode
          className={`${styles.nodeWrapper} ${animateClass} ${localMinimizeLevel === "minimum" ? styles.minimized : localMinimizeLevel === "medium" ? styles.minifiedInfo : ""} ${data.isMinimizing ? styles.minimizing : ""} ${data.isMaximizing ? styles.maximizing : ""} ${isNodeHovered ? "node-ns-resize-cursor" : ""} ${
            // Apply status indicator classes directly to BaseNode
            currentStatus === "success"
              ? "border-green-500 border-2"
              : currentStatus === "loading"
                ? "border-blue-500 border-2"
                : currentStatus === "error"
                  ? "border-red-500 border-2"
                  : ""
            } ${
            // Apply search match glow classes
            data?.isCurrentSearchMatch ? styles.currentSearchMatch :
              data?.isSearchMatch ? styles.searchMatch : ""
            }`}
          data-shadow={classificationKey}
          data-tour={dataTour}
          style={{
            ...customStyle,
            display: "flex",
            flexDirection: "column",
            position: "relative",
            minWidth:
              localMinimizeLevel === "minimum"
                ? "300px"
                : localMinimizeLevel === "medium"
                  ? "350px"
                  : "400px",
            width: "100%",
            height: "100%"
          }}
          selected={selected}
          disableDefaultShadow={true}
          onDoubleClick={handleNodeDoubleClick}
          onMouseEnter={handleNodeMouseEnter}
          onMouseLeave={handleNodeMouseLeave}
          data-minimize-level={localMinimizeLevel}
        >
          {/* Remove this duplicate border element */}
          <NodeHeader className="-mx-0 -mt-2 border-b h-12 m-2">
            {levelOfUsage &&
              onLevelOfUsageChange &&
              (isNodeHovered || selected) && (
                <button
                  onClick={handleNextMode}
                  className="mr-2 px-2 py-1 rounded border hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-xs"
                  title="Switch Level of Usage Mode"
                >
                  {levelOfUsage === "Guided" && "🔒 Guided"}
                  {levelOfUsage === "Open" && "🔓 Open"}
                  {levelOfUsage === "TotalFreedom" && "🌐 Total Freedom"}
                </button>
              )}
            <NodeHeaderTitle
              onClick={handleTitleClick}
              style={{
                cursor: !isProcessing && !localIsLocked ? "pointer" : "default",
                display: "flex",
                alignItems: "center",
                // Fix title wrapping in minimized modes
                flex: "1 1 0",
                minWidth: 0,
                overflow: "hidden"
              }}
            >
              {isEditingTitle ? (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    flex: 1,
                    minWidth: 0
                  }}
                >
                  {(showLockIcon || labelLocked) && (
                    <button
                      type="button"
                      aria-label={labelLocked ? "Unlock Title" : "Lock Title"}
                      title={labelLocked ? "Unlock Title" : "Lock Title"}
                      onClick={handleLabelLockToggle}
                      className={styles.nodeIconButton}
                      tabIndex={-1}
                    >
                      {labelLocked ? (
                        <Lock size={18} color="#f59e42" />
                      ) : (
                        <Unlock size={18} color="#888" />
                      )}
                    </button>
                  )}
                  <input
                    ref={titleInputRef}
                    type="text"
                    value={editingTitleValue}
                    onChange={(e) => setEditingTitleValue(e.target.value)}
                    onBlur={handleTitleSave}
                    onKeyDown={handleTitleKeyPress}
                    className="nodrag w-full px-2 py-1 border border-gray-300 rounded"
                    style={{
                      fontSize: "inherit",
                      fontWeight: "inherit",
                      flex: 1,
                      minWidth: 0,
                      width: "100%",
                      maxWidth: "100%"
                    }}
                  />
                </div>
              ) : (
                <span
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    flex: "1 1 0",
                    minWidth: 0,
                    overflow: "hidden"
                  }}
                >
                  {(showLockIcon || labelLocked) && (
                    <button
                      type="button"
                      aria-label={labelLocked ? "Unlock Title" : "Lock Title"}
                      title={labelLocked ? "Unlock Title" : "Lock Title"}
                      onClick={handleLabelLockToggle}
                      className={styles.nodeIconButton}
                      tabIndex={-1}
                    >
                      {labelLocked ? (
                        <Lock size={18} color="#f59e42" />
                      ) : (
                        <Unlock size={18} color="#888" />
                      )}
                    </button>
                  )}
                  <span
                    style={{
                      // Prevent title wrapping by truncating long titles
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      flex: "1 1 0",
                      minWidth: 0,
                      fontSize: "18px",
                      fontWeight: "lighter"
                    }}
                  >
                    {!nodeTitle || nodeTitle.trim() === ""
                      ? "Not defined"
                      : nodeTitle}
                  </span>
                </span>
              )}
            </NodeHeaderTitle>
            <div className="flex items-center mr-2">
              {(isNodeHovered || selected) && (
                <NodePresenceWrapper nodeId={id} activeUsers={activeUsers} />
              )}
            </div>
            {(isNodeHovered || selected) && (
              <NodeHeaderActions>
                {/* Quick minimize/maximize button */}
                <button
                  onClick={handleMinimizeToggle}
                  disabled={isProcessing}
                  className="mr-1 p-1 rounded-sm hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors duration-200 node-btn-cursor"
                  style={{ cursor: isProcessing ? "default" : "pointer" }}
                  title={
                    localMinimizeLevel === "normal"
                      ? "Minimize to Info"
                      : localMinimizeLevel === "medium"
                        ? "Minimize to Icon"
                        : "Maximize"
                  }
                >
                  {localMinimizeLevel === "minimum" ? (
                    <Maximize className="h-4 w-4" />
                  ) : (
                    <Minimize className="h-4 w-4" />
                  )}
                </button>
                {/* Custom User Instructions button */}
                <button
                  onClick={() => setShowInstructionsDialog(true)}
                  disabled={isProcessing || localIsLocked}
                  className="mr-1 p-1 rounded-sm hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors duration-200 node-btn-cursor"
                  title="Set Custom User Instructions"
                  aria-label="Set Custom User Instructions"
                  style={{
                    cursor:
                      isProcessing || localIsLocked ? "default" : "pointer"
                  }}
                >
                  {customInstructions?.trim() ? (
                    <LucideScrollText className="h-4 w-4 text-yellow-600" />
                  ) : (
                    <LucideScroll className="h-4 w-4" />
                  )}
                </button>
                {/* Temperature icon button */}
                <button
                  onClick={handleTemperatureDialogOpen}
                  disabled={isProcessing || localIsLocked}
                  className="mr-1 p-1 rounded-sm hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors duration-200 node-btn-cursor"
                  title="Set Node-specific AI Temperature"
                  aria-label="Set Node-specific AI Temperature"
                  style={{
                    cursor:
                      isProcessing || localIsLocked ? "default" : "pointer"
                  }}
                >
                  <Thermometer
                    className="h-4 w-4"
                    color={
                      typeof data?.customcreativeness === "number"
                        ? "#f59e42"
                        : "#888"
                    }
                  />
                </button>
                <NodeConnectionMenu
                  nodeId={id}
                  onConnect={handleConnect}
                  onDisconnect={handleDisconnect}
                />
                <NodeHeaderMenuAction label="Actions">
                  <DropdownMenuLabel>Actions</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={handleMinimizeToggle}
                    disabled={isProcessing}
                  >
                    {localMinimizeLevel === "normal" ? (
                      <>
                        <Minimize className="mr-2 h-4 w-4" />
                        <span>Minimize to Info</span>
                      </>
                    ) : localMinimizeLevel === "medium" ? (
                      <>
                        <Minimize className="mr-2 h-4 w-4" />
                        <span>Minimize to Icon</span>
                      </>
                    ) : (
                      <>
                        <Maximize className="mr-2 h-4 w-4" />
                        <span>Maximize</span>
                      </>
                    )}
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={handleLock}
                    disabled={isProcessing}
                  >
                    {localIsLocked ? (
                      <>
                        <Unlock className="mr-2 h-4 w-4" />
                        <span>Unlock</span>
                      </>
                    ) : (
                      <>
                        <Lock className="mr-2 h-4 w-4" />
                        <span>Lock</span>
                      </>
                    )}
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={handleDuplicate}
                    disabled={isProcessing}
                  >
                    <Copy className="mr-2 h-4 w-4" />
                    <span>Duplicate</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={handleGenerateTitle}
                    disabled={isProcessing}
                  >
                    <Rocket className="mr-2 h-4 w-4" />
                    <span>Generate new title</span>
                  </DropdownMenuItem>                  <DropdownMenuItem
                    onClick={handleExtractDIKW}
                    disabled={isProcessing}
                  >
                    <BrainCircuit className="mr-2 h-4 w-4" />
                    <span>Extract DIKW layers</span>
                  </DropdownMenuItem>
                  {data?.DIKW && (
                    <DropdownMenuItem
                      onClick={handleViewDIKW}
                      disabled={isProcessing}
                    >
                      <BrainCircuit className="mr-2 h-4 w-4" />
                      <span>View DIKW layers</span>
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuLabel>Settings</DropdownMenuLabel>
                  <DropdownMenuItem
                    onClick={handleAssignUsers}
                    disabled={isProcessing || localIsLocked}
                  >
                    <UserPlus className="mr-2 h-4 w-4" />
                    <span>Assign Users</span>
                  </DropdownMenuItem>
                  {onConfigClick && (
                    <DropdownMenuItem
                      onClick={onConfigClick}
                      disabled={isProcessing || localIsLocked}
                    >
                      <Settings className="mr-2 h-4 w-4" />
                      <span>Configuration</span>
                    </DropdownMenuItem>
                  )}
                  {onDataSettingsClick && (
                    <DropdownMenuItem
                      onClick={onDataSettingsClick}
                      disabled={isProcessing || localIsLocked}
                    >
                      <Filter className="mr-2 h-4 w-4" />
                      <span>Data Settings</span>
                    </DropdownMenuItem>
                  )}
                  {onToggleDebugInfo && (
                    <DropdownMenuItem
                      onClick={onToggleDebugInfo}
                      disabled={isProcessing || localIsLocked}
                    >
                      <Code className="mr-2 h-4 w-4" />
                      <span>
                        {showDebugInfo ? "Hide Debug Info" : "Show Debug Info"}
                      </span>
                    </DropdownMenuItem>
                  )}
                  {/* Only show history button if hovered or selected */}
                  {enableHistory &&
                    id &&
                    projectId &&
                    (isNodeHovered || selected) && (
                      <DropdownMenuItem
                        onClick={() => setIsHistoryDialogOpen(true)}
                      >
                        <History className="mr-2 h-4 w-4" />
                        <span>History</span>
                      </DropdownMenuItem>
                    )}
                  <DropdownMenuSeparator />
                  {customButtons.length > 0 && (
                    <>
                      <DropdownMenuLabel>Special</DropdownMenuLabel>
                      {customButtons.map((button) => (
                        <DropdownMenuItem
                          key={button.id}
                          onClick={button.onClick}
                          disabled={isProcessing || localIsLocked}
                        >
                          <span className="mr-2">{button.icon}</span>
                          <span>{button.tooltip}</span>
                        </DropdownMenuItem>
                      ))}
                      <DropdownMenuSeparator />
                    </>
                  )}
                  <DropdownMenuItem
                    onClick={handleDelete}
                    disabled={isProcessing}
                    className="text-red-600"
                  >
                    <Trash className="mr-2 h-4 w-4" />
                    <span>Delete</span>
                  </DropdownMenuItem>
                </NodeHeaderMenuAction>
              </NodeHeaderActions>
            )}
          </NodeHeader>
          <div className="pl-4 pr-4">
            {/* Show Custom User Instructions below the header, on its own row */}
            {customInstructions?.trim() && localMinimizeLevel === "normal" && (
              <div className="mt-2 mb-2 px-3 py-2 rounded bg-yellow-50 border border-yellow-200">
                <div className="font-semibold text-yellow-700 mb-1" style={{ fontSize: "18px", fontWeight: "lighter" }}>
                  Custom User Instructions
                </div>
                <div className="text-sm text-yellow-900 whitespace-pre-line" style={{ fontSize: "14px", fontWeight: "lighter" }}>
                  {customInstructions}
                </div>
              </div>
            )}
            {/* Show Custom Temperature below the header when active */}
            {typeof data?.customcreativeness === "number" &&
              localMinimizeLevel === "normal" && (
                <div className="mt-2 mb-2 px-3 py-2 rounded bg-orange-50 border border-orange-200">
                  <div className="font-semibold text-orange-700 mb-1" style={{ fontSize: "18px", fontWeight: "lighter" }}>
                    Custom AI Temperature
                  </div>
                  <div className="text-sm text-orange-900" style={{ fontSize: "14px", fontWeight: "lighter" }}>
                    Temperature override:{" "}
                    <span className="font-mono">
                      {Math.round((data.customcreativeness || 0) * 100)}%
                    </span>
                    <span className="ml-2 text-xs">
                      (
                      {data.customcreativeness < 0.3
                        ? "Focused"
                        : data.customcreativeness > 0.7
                          ? "Creative"
                          : "Balanced"}
                      )
                    </span>{" "}
                  </div>
                </div>
              )}{" "}
            {/* Show Analysis Status when enabled - positioned like Custom User Instructions */}
            {analysisAndRecommendations && localMinimizeLevel === "normal" && (
              <div
                className="mt-2 mb-2 px-3 py-2 rounded bg-blue-50 border border-blue-200 cursor-pointer hover:bg-blue-100 transition-colors"
                onClick={() => setIsAnalysisDialogOpen(true)}
              >
                <div className="font-semibold text-blue-700 mb-1" style={{ fontSize: "18px", fontWeight: "lighter" }}>
                  Analysis & Recommendations
                </div>
                <div className="text-sm text-blue-900" style={{ fontSize: "14px", fontWeight: "lighter" }}>
                  {analysisResults ? (
                    <>
                      Quality:{" "}
                      <span className="font-mono font-semibold">
                        {analysisResults.qualityRating}
                      </span>
                      {analysisResults.score && (
                        <span className="ml-2">
                          ({analysisResults.score}/100)
                        </span>
                      )}
                      {lastAnalyzedAt && (
                        <div className="text-xs text-blue-700 mt-1">
                          Last analyzed:{" "}
                          {new Date(lastAnalyzedAt).toLocaleString()}
                        </div>
                      )}
                    </>
                  ) : (
                    <span className="text-blue-700">
                      Analysis enabled - Click "Run Analysis" to analyze content
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>
          {localMinimizeLevel === "medium" && renderMinifiedInfo()}
          {/* Fix: Use opacity/visibility instead of display:none */}
          <div
            style={{
              flex: localMinimizeLevel === "normal" ? "1 1 auto" : "0 0 0",
              opacity: localMinimizeLevel === "normal" ? 1 : 0,
              visibility:
                localMinimizeLevel === "normal" ? "visible" : "hidden",
              overflow: localMinimizeLevel === "normal" ? "auto" : "hidden",
              transition: "opacity 0.15s ease-out",
              width: "100%",
              minHeight: localMinimizeLevel === "normal" ? 0 : 0,
              maxHeight: localMinimizeLevel === "normal" ? "none" : 0,
              flexDirection: "column",
              display: "flex", // Always keep display:flex, never none
              padding: "5px",
            }}
            ref={setRefs}
            className="nodeContent"
          >
            {children}
          </div>
          {/* Node Footer */}
          <div className={`${styles.nodeFooter} rounded-b-md`}>
            {/* Left: Action Counter */}
            <AIUsageIndicator
              className={styles.nodeFooterActionCount}
              projectId={projectId}
              nodeId={id}
              actionsTotal={data?.actionsTotal || 0}
            />
            {/* Center: History Navigation */}
            <div className={styles.nodeFooterCenter}>
              <button
                className={styles.nodeFooterButton}
                onClick={() => undoManager.undo()}
                disabled={!(undoManager && undoManager.undoStack && undoManager.undoStack.length > 0)}
                title="Previous State"
                type="button"
              >
                <History size={18} />
              </button>
              {/* NodeHistoryDialog trigger button (centered between back/forward) */}
              {enableHistory && id && projectId && (
                <NodeHistoryDialog
                  nodeId={id}
                  projectId={projectId}
                  currentNodeData={data}
                  currentNodeType={data?.type || 'unknown'}
                  onRestoreState={handleHistoryRestore}
                  onDuplicateNode={handleHistoryDuplicate}
                  className={styles.nodeFooterActionCount}
                  showTrigger={true}
                />
              )}
              <button
                className={styles.nodeFooterButton}
                onClick={() => undoManager.redo()}
                disabled={!(undoManager && undoManager.redoStack && undoManager.redoStack.length > 0)}
                title="Next State"
                type="button"
              >
                <History size={18} style={{ transform: 'scaleX(-1)' }} />
              </button>
            </div>
            {/* Right: Reserved for future */}
            <div style={{ width: 32 }} />
          </div>
          {resizable &&
            !localIsLocked &&
            !isProcessing &&
            localMinimizeLevel === "normal" &&
            (isNodeHovered || selected) && (
              <NodeResizeControl
                className={styles.nodeResizeControl}
                style={controlStyle}
                minWidth={minWidth}
                minHeight={minHeight}
                onResizeStart={() => startResize(id)}
                onResizeEnd={onResizeEnd}
              >
                <ResizeIcon />
              </NodeResizeControl>)}
        </BaseNode>
        <AlertDialog
          open={isDeleteDialogOpen}
          onOpenChange={setIsDeleteDialogOpen}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
              <AlertDialogDescription>
                This action cannot be undone. This will permanently delete the
                node.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={confirmDelete}>
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
        {/* Custom User Instructions Dialog */}
        {showInstructionsDialog && (
          <AlertDialog
            open={showInstructionsDialog}
            onOpenChange={setShowInstructionsDialog}
          >
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Custom User Instructions</AlertDialogTitle>
                <AlertDialogDescription>
                  Add high-level instructions for the AI to follow when
                  processing this node. These will be prioritized for this node
                  type.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <textarea
                className="w-full border rounded p-2 mt-2"
                rows={5}
                value={customInstructions}
                onChange={(e) => setCustomInstructions(e.target.value)}
                placeholder="e.g. Always use formal tone. Prioritize security best practices."
                title="Custom User Instructions"
                aria-label="Custom User Instructions"
              />
              <div className="flex justify-end gap-2 mt-4">
                <button
                  className="px-3 py-1 rounded border bg-gray-100 hover:bg-gray-200"
                  onClick={handleInstructionsCancel}
                  type="button"
                >
                  Cancel
                </button>
                <button
                  className="px-3 py-1 rounded border bg-blue-600 text-white hover:bg-blue-700"
                  onClick={() => handleInstructionsSave(customInstructions)}
                  type="button"
                >
                  Save
                </button>
              </div>
            </AlertDialogContent>
          </AlertDialog>
        )}
        {/* Node-specific AI Temperature Dialog */}
        {showTemperatureDialog && (
          <AlertDialog
            open={showTemperatureDialog}
            onOpenChange={setShowTemperatureDialog}
          >
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>
                  Node-specific AI Temperature
                </AlertDialogTitle>
                <AlertDialogDescription>
                  <div className="mb-2">
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={bypassDefaultTemperature}
                        onChange={(e) =>
                          setBypassDefaultTemperature(e.target.checked)
                        }
                        className="mr-2"
                      />
                      I want to bypass default temperature for this node
                    </label>
                  </div>
                  <div className="flex items-center gap-2 mb-2">
                    <input
                      type="range"
                      min={0}
                      max={100}
                      step={1}
                      value={localTemperature}
                      onChange={(e) =>
                        setLocalTemperature(Number(e.target.value))
                      }
                      disabled={!bypassDefaultTemperature}
                      style={{ width: "70%" }}
                    />
                    <span className="font-mono text-yellow-700">
                      {localTemperature}
                    </span>
                  </div>
                  <div className="text-xs text-gray-500">
                    Value stored:{" "}
                    <span className="font-mono">
                      {bypassDefaultTemperature
                        ? (localTemperature / 100).toFixed(2)
                        : "default"}
                    </span>
                  </div>
                  <div className="text-xs text-gray-500 mt-2">
                    Lower values (0) = more deterministic, higher values (1) =
                    more creative/random.
                  </div>
                </AlertDialogDescription>
              </AlertDialogHeader>
              <div className="flex justify-end gap-2 mt-4">
                <button
                  className="px-3 py-1 rounded border bg-gray-100 hover:bg-gray-200"
                  onClick={handleTemperatureDialogClose}
                  type="button"
                >
                  Cancel
                </button>
                <button
                  className="px-3 py-1 rounded border bg-blue-600 text-white hover:bg-blue-700"
                  onClick={handleTemperatureSave}
                  type="button"
                >
                  Save
                </button>
              </div>
            </AlertDialogContent>
          </AlertDialog>
        )}{" "}
        {/* Node History Dialog */}
        {/* NodeHistoryDialog modal (no trigger) for external open/close */}
        {enableHistory && id && projectId && (
          <NodeHistoryDialog
            nodeId={id}
            projectId={projectId}
            currentNodeData={data}
            currentNodeType={data?.type || "unknown"}
            onRestoreState={handleHistoryRestore}
            onDuplicateNode={handleHistoryDuplicate}
            externalOpen={isHistoryDialogOpen}
            onExternalOpenChange={setIsHistoryDialogOpen}
            showTrigger={false}
          />
        )}{" "}
        {/* Node Analysis and Recommendations Dialog */}
        {analysisAndRecommendations && analysisResults && (
          <NodeAnalysisAndRecommendationsDialog
            open={isAnalysisDialogOpen}
            onOpenChange={setIsAnalysisDialogOpen}
            analysisResults={analysisResults}
            onCreateRecommendedNodes={handleCreateRecommendedNodes}
            onCopyToContext={handleCopyToContext}
            currentNodeId={id} // Pass the current node's id
            updateNodeData={updateNodeData} // Pass the updateNodeData function
          />)}
        {/* DIKW Dialog */}
        <DIKWDialog
          open={isDIKWDialogOpen}
          onOpenChange={setIsDIKWDialogOpen}
          dikwData={data?.DIKW || null}
          onClose={() => setIsDIKWDialogOpen(false)}
        />
        {/* Keep existing handles for data flow and links */}
        <BaseHandle
          type="target"
          position={Position.Left}
          id={`${id}-input-standard`}
          className={styles.baseHandleTarget}
        />
        <BaseHandle
          type="source"
          position={Position.Right}
          id={`${id}-output-standard`}
          className={styles.baseHandleSource}
        />
        <BaseHandle
          type="target"
          position={Position.Left}
          id={`${id}-input-aiknot`}
          className={styles.dataFlowHandleTarget}
        />
        <BaseHandle
          type="source"
          position={Position.Right}
          id={`${id}-output-aiknot`}
          className={styles.dataFlowHandleSource}
        />
      </>
    )
  }
)

interface NodePresenceWrapperProps {
  nodeId: string
  activeUsers?: Record<string, any>
}

const NodePresenceWrapper: React.FC<NodePresenceWrapperProps> = React.memo(
  ({ nodeId, activeUsers }) => {
    const usersString = useMemo(
      () => JSON.stringify(activeUsers || {}),
      [activeUsers]
    )

    const hasUsers = useMemo(
      () => !!activeUsers && Object.keys(activeUsers).length > 0,
      [usersString]
    )

    if (!hasUsers) return null

    return (
      <PresenceIndicator
        activeUsers={activeUsers}
        maxDisplayed={3}
        showDetails={true}
        size="md"
        currentNodeId={nodeId}
      />
    )
  }
)

function ResizeIcon() {
  return (
    <div
      style={{
        position: "absolute",
        right: 1,
        bottom: -1,
        cursor: "se-resize",
        ...controlStyle
      }}
      className="nodrag nowheel"
    >
      <TriangleRight size={20} style={{ color: "#ea9a22bb" }} />
    </div>
  )
}

export const NodeCustomActions: React.FC<{
  buttons: CustomButton[]
  nodeId: string
}> = ({ buttons }) => {
  return null
}

export default memo(NodeBaseContainer)
// Define custom styles for BaseHandle types
const baseHandleTargetStyle = {
  left: -6,
  top: 6,
  background: "#000",
  border: "2px solid #fff",
  width: 8,
  height: 8,
  borderRadius: 2,
  transform: "rotate(45deg)",
  boxShadow: "0 0 2px #2563eb55",
  zIndex: -2
}

const baseHandleSourceStyle = {
  right: -6,
  top: 6,
  background: "#fff",
  border: "2px solid #000",
  width: 8,
  height: 8,
  borderRadius: 2,
  transform: "rotate(45deg)",
  boxShadow: "0 0 2px #f59e4255",
  zIndex: -2
}

